import { createContext, useContext, useState, useCallback, useMemo, useRef, useEffect, type ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { driver, type Driver, type Config } from 'driver.js'
import { useAuth } from './AuthContext'
import { buildTourSteps, type TourStep } from '../tour/tourSteps'
import '../tour/tourStyles.css'

const STORAGE_KEY = 'pcl_tour_v1'

/** Delay (ms) after navigation to let lazy-loaded pages mount */
const NAV_DELAY = 600

/** Read current path without stale closure issues */
function currentPath() {
  return window.location.pathname
}

interface TourContextType {
  startTour: () => void
  isTourActive: boolean
  hasCompletedTour: boolean
}

const TourContext = createContext<TourContextType | undefined>(undefined)

export function TourProvider({ children }: { children: ReactNode }) {
  const { t } = useTranslation()
  const { user } = useAuth()
  const navigate = useNavigate()

  const [isTourActive, setIsTourActive] = useState(false)
  const [hasCompletedTour] = useState(() => localStorage.getItem(STORAGE_KEY) !== null)
  const driverRef = useRef<Driver | null>(null)
  const stepsRef = useRef<TourStep[]>([])
  const activeIndexRef = useRef(0)
  const autoStartedRef = useRef(false)

  const isAdmin = user?.role === 'Admin'

  const markCompleted = useCallback(() => {
    localStorage.setItem(STORAGE_KEY, new Date().toISOString())
  }, [])

  const destroyDriver = useCallback(() => {
    if (driverRef.current) {
      driverRef.current.destroy()
      driverRef.current = null
    }
    setIsTourActive(false)
  }, [])

  /** Navigate to route if not already there, then run callback after mount */
  const navigateAndRun = useCallback((route: string, cb: () => void) => {
    if (currentPath() === route) {
      cb()
    } else {
      navigate(route)
      setTimeout(cb, NAV_DELAY)
    }
  }, [navigate])

  const startTour = useCallback(() => {
    // Build fresh steps with current translations
    const steps = buildTourSteps(t, isAdmin)
    stepsRef.current = steps
    activeIndexRef.current = 0

    const totalSteps = steps.length

    const driverConfig: Config = {
      showProgress: true,
      animate: true,
      smoothScroll: true,
      allowClose: true,
      overlayOpacity: 0.7,
      overlayClickBehavior: () => {},
      stagePadding: 8,
      stageRadius: 8,
      popoverOffset: 12,
      showButtons: ['next', 'previous', 'close'],
      progressText: `{{current}} / {{total}}`,
      nextBtnText: t('tour.buttons.next'),
      prevBtnText: t('tour.buttons.prev'),
      doneBtnText: t('tour.buttons.done'),

      steps: steps.map(({ route: _route, ...rest }) => rest),

      onNextClick: () => {
        const nextIndex = activeIndexRef.current + 1
        if (nextIndex >= totalSteps) {
          markCompleted()
          destroyDriver()
          return
        }

        const nextStep = steps[nextIndex]
        if (nextStep.route) {
          navigateAndRun(nextStep.route, () => {
            activeIndexRef.current = nextIndex
            driverRef.current?.moveTo(nextIndex)
          })
        } else {
          activeIndexRef.current = nextIndex
          driverRef.current?.moveNext()
        }
      },

      onPrevClick: () => {
        const prevIndex = activeIndexRef.current - 1
        if (prevIndex < 0) return

        const prevStep = steps[prevIndex]
        if (prevStep.route) {
          navigateAndRun(prevStep.route, () => {
            activeIndexRef.current = prevIndex
            driverRef.current?.moveTo(prevIndex)
          })
        } else {
          activeIndexRef.current = prevIndex
          driverRef.current?.movePrevious()
        }
      },

      onDestroyStarted: () => {
        markCompleted()
        destroyDriver()
      },
    }

    // Ensure we start from Events page
    const firstRouteStep = steps.find(s => s.route)
    const startRoute = firstRouteStep?.route || '/events'

    navigateAndRun(startRoute, () => {
      const d = driver(driverConfig)
      driverRef.current = d
      setIsTourActive(true)
      d.drive()
    })
  }, [t, isAdmin, navigateAndRun, markCompleted, destroyDriver])

  // Auto-start tour for new users (only once per session)
  useEffect(() => {
    if (
      user &&
      !autoStartedRef.current &&
      localStorage.getItem(STORAGE_KEY) === null
    ) {
      autoStartedRef.current = true
      // Delay to let the initial page fully render
      const timer = setTimeout(startTour, 1200)
      return () => clearTimeout(timer)
    }
  }, [user, startTour])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      driverRef.current?.destroy()
    }
  }, [])

  const value = useMemo(() => ({
    startTour,
    isTourActive,
    hasCompletedTour,
  }), [startTour, isTourActive, hasCompletedTour])

  return (
    <TourContext.Provider value={value}>
      {children}
    </TourContext.Provider>
  )
}

const fallbackTour: TourContextType = {
  startTour: () => {},
  isTourActive: false,
  hasCompletedTour: false,
}

export function useTour() {
  const context = useContext(TourContext)
  return context ?? fallbackTour
}
